import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import {
  UsageBarAlert,
  UsageBarErrorMessage,
  UsageBarGraph,
  UsageBarLegend,
} from './components';

const useStyles = makeStyles((theme) => {
  return {
    expandMoreIcon: {
      color: theme.palette.greyBlue[700],
    },
    accordion: {
      '& .Mui-focusVisible': {
        outlineOffset: theme.spacing(-0.25),
      },
      '& .MuiAccordionSummary-root.Mui-expanded': {
        minHeight: ({ isRemainingAllowanceNegative }) =>
          isRemainingAllowanceNegative
            ? theme.spacing(8.5)
            : theme.spacing(5.5),
      },
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: theme.spacing(0, 0),
      },
    },
    accordionExpanded: {
      marginBottom: '0px !important',
    },
    accordionSummaryContent: {
      display: 'flex',
      flexDirection: 'column',
    },
    accordionSummary: {
      '& .MuiIconButton-root': {
        position: 'relative',
        top: ({ isRemainingAllowanceNegative }) =>
          isRemainingAllowanceNegative
            ? `-${theme.spacing(1.875)}px`
            : `${theme.spacing(0)}px`,
      },
    },
    label: {
      fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
      fontSize: theme.spacing(1.75),
      fontWeight: '500',
      lineHeight: `${theme.spacing(2.625)}px`,
    },
    accordionDetails: {
      marginTop: `-${theme.spacing(0.1)}em`,
      paddingTop: 0,

      display: 'flex',
      flexDirection: 'column',
    },
  };
});

const UsageBar = ({
  accordionLabel = null,
  alert = {},
  defaultExpanded = false,
  errorLabel = null,
  hasAccordion = false,
  id,
  usageText,
  // DRM usage values
  availablePercent = 0,
  currentPercent = 0,
  newPercent = 0,
  remainingAllowance = 0,
  ...props
}) => {
  const drm = {
    currentPercent,
    newPercent,
    availablePercent,
    remainingAllowance,
    usageText,
  };

  const isRemainingAllowanceNegative = newPercent > remainingAllowance; // check if new selection exceeds remaining allowance

  const classes = useStyles({ isRemainingAllowanceNegative });

  const progressBarJsx = (
    <>
      <UsageBarGraph drm={drm} id={id} />
      {isRemainingAllowanceNegative && errorLabel ? (
        <UsageBarErrorMessage errorLabel={errorLabel} />
      ) : null}
    </>
  );

  const informationJsx = (
    <>
      <UsageBarLegend
        drm={{
          ...drm,
          shouldShowNewPercent: Boolean(alert && alert?.menu === 'print'),
        }}
        id={id}
      />
      {alert.menu && (
        <UsageBarAlert alert={alert} percent={drm.availablePercent} />
      )}
    </>
  );

  return hasAccordion ? (
    <Accordion
      classes={{ expanded: classes.accordionExpanded, root: classes.accordion }}
      defaultExpanded={defaultExpanded}
      elevation={0}
      id={`usage-bar-accordion-${id}`}
      {...props}
    >
      {
        // MUI pulls props from 1st child here (h3) and slaps it as aria-labelledby and id to accordionDetails
      }
      <h3
        aria-controls={`usage-bar-info-panel-${id}`}
        id={`usage-bar-accordion-header-${id}`}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandMoreIcon} />}
          id={`usage-bar-accordion-summary-${id}`}
          classes={{
            root: classes.accordionSummary,
            content: classes.accordionSummaryContent,
            expanded: classes.accordionSummaryExpanded,
          }}
          IconButtonProps={{ disabled: null }} // fixes an accessibility violation
        >
          {Boolean(accordionLabel) && (
            <span className={classes.label}>{accordionLabel}</span>
          )}
          {progressBarJsx}
        </AccordionSummary>
      </h3>
      <AccordionDetails className={classes.accordionDetails}>
        {informationJsx}
      </AccordionDetails>
    </Accordion>
  ) : (
    <Box id={`usage-bar-${id}`} className={classes.defaultContainer} {...props}>
      {progressBarJsx}
      {informationJsx}
    </Box>
  );
};

UsageBar.propTypes = {
  /**
   *  Props for UsageBarAlert
   */
  alert: PropTypes.shape({}),
  /**
   *  Should accordion be open by default
   */
  defaultExpanded: PropTypes.bool,
  /**
   *  Error label below progressBar
   */
  errorLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Should usageBar display as an accordion
   */
  hasAccordion: PropTypes.bool,
  /**
   * ID, passed to UsageBarGraph and UsageBarLegend
   */
  id: PropTypes.string.isRequired,
  /**
   *  Menu-specific label for list in UsageBarLegend
   */
  usageText: PropTypes.string.isRequired,

  /**
   *  Percent of available DRM allowed, in decimal format
   */
  availablePercent: PropTypes.number.isRequired,
  /**
   *  Percent of DRM that has been used, in decimal format
   */
  currentPercent: PropTypes.number.isRequired,
  /**
   *  Percent of DRM user is attempting to add, in decimal format
   */
  newPercent: PropTypes.number,
  /**
   *  Percent of DRM that is remaining, in decimal format
   */
  remainingAllowance: PropTypes.number.isRequired,
};

export default UsageBar;
